import { useState, useEffect } from "react";

// Keep this outside to persist across re-renders
let deferredPrompt = null;

const INSTALLED_KEY = "eventsapp.pwa.installed";

export const usePWA = () => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [wasInstalled, setWasInstalled] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(INSTALLED_KEY) === "true"
  );
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);
  const [isOnline, setIsOnline] = useState(
    typeof window !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    // Online / offline status
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Service Worker State Tracking (registration happens in app's index.js)
    if ("serviceWorker" in navigator) {
      const bindRegistration = async () => {
        try {
          const reg =
            (await navigator.serviceWorker.getRegistration("/")) ||
            (await navigator.serviceWorker.ready);

          setSwRegistration(reg);

          // Check if there is already a waiting worker on load
          if (reg?.waiting) {
            setUpdateAvailable(true);
          }

          reg?.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                }
              });
            }
          });
        } catch (err) {
          console.error("Unable to access service worker registration:", err);
        }
      };

      bindRegistration();
    }

    // PWA Install Logic
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      deferredPrompt = e;
      window.__eventsappDeferredInstallPrompt = e;
      // Update UI notify the user they can install the PWA
      setIsInstallable(true);
    };

    const handleInstallableSignal = () => {
      if (window.__eventsappDeferredInstallPrompt) {
        deferredPrompt = window.__eventsappDeferredInstallPrompt;
        setIsInstallable(true);
      }
    };

    const handleAppInstalled = () => {
      // Clean up when the app is successfully installed (manually or via prompt)
      setIsInstallable(false);
      setWasInstalled(true);
      localStorage.setItem(INSTALLED_KEY, "true");
      deferredPrompt = null;
      window.__eventsappDeferredInstallPrompt = null;
      console.log("PWA was installed");
    };

    if (window.__eventsappDeferredInstallPrompt) {
      deferredPrompt = window.__eventsappDeferredInstallPrompt;
      setIsInstallable(true);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("eventsapp:installable", handleInstallableSignal);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("eventsapp:installable", handleInstallableSignal);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!deferredPrompt) {
      console.warn("Install prompt sampled but deferredPrompt is null");
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to the install prompt: ${outcome}`);

    if (outcome === "accepted") {
      deferredPrompt = null;
      setIsInstallable(false);
    }
  };

  const reloadToUpdate = () => {
    if (swRegistration?.waiting) {
      // Send message to SW to skip waiting and activate
      swRegistration.waiting.postMessage({ type: "SKIP_WAITING" });
    } else {
      window.location.reload();
    }
  };

  return {
    isInstallable,
    wasInstalled,
    triggerInstall,
    updateAvailable,
    reloadToUpdate,
    isOnline,
  };
};
